import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { RequestDigitisationService } from '®shared/container-structure/edit/request-digitisation/request-digitisation.service';
import { isbnsToUniqueIsbn13s } from '®shared/isbns/isbn-utils';
import { UriService } from '®shared/url-utils';

import {
  CancelRequestDigitisationAction,
  CompleteRequestDigitisationAction,
  FetchDcSettingsFailedAction,
  FetchDcSettingsSuccessAction,
  HideRequestDigitisationModalAction,
  OpenRequestDigitisationIdentifierPickerAction,
  OpenRequestDigitisationUrlAction,
  RequestDigitisationActionTypes,
  RequestDigitisationCloseActions,
  RequestDigitisationFinishedReason,
  RequestDigitisationOpenActions,
  SelectRequestDigitisationIdentifierAction,
  ShowRequestDigitisationModalAction,
} from './request-digitisation.actions';

@Injectable()
export class RequestDigitisationEffects {
  constructor(
    private actions$: Actions,
    private uriService: UriService,
    private service: RequestDigitisationService,
  ) {}

  requestDigitisationOpen$: Observable<RequestDigitisationOpenActions> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(RequestDigitisationActionTypes.SHOW_REQUEST_DIGITISATION_MODAL),
        map((action: ShowRequestDigitisationModalAction) => {
          const { item, user } = action.payload;

          const resource = item.resource;
          if (
            resource &&
            (isbnsToUniqueIsbn13s(resource.isbns).length > 1 ||
              resource.dois.length > 1)
          ) {
            return new OpenRequestDigitisationIdentifierPickerAction({
              item,
              user,
            });
          }
          const requestDigitisationUrl =
            this.uriService.getRequestDigitisationLink(item.id, user);
          return new OpenRequestDigitisationUrlAction({
            requestDigitisationUrl,
            item,
          });
        }),
      ),
    );

  requestDigitisationSelectIdentifier$: Observable<OpenRequestDigitisationUrlAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(
          RequestDigitisationActionTypes.SELECT_REQUEST_DIGITISATION_IDENTIFIER,
        ),
        map((action: SelectRequestDigitisationIdentifierAction) => {
          const { item, user, selectedIdentifier } = action.payload;

          const requestDigitisationUrl =
            this.uriService.getRequestDigitisationLink(
              item.id,
              user,
              selectedIdentifier,
            );
          return new OpenRequestDigitisationUrlAction({
            requestDigitisationUrl,
            item,
          });
        }),
      ),
    );

  requestDigitisationHide$: Observable<RequestDigitisationCloseActions> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(RequestDigitisationActionTypes.HIDE_REQUEST_DIGITISATION_MODAL),
        map((action: HideRequestDigitisationModalAction) => {
          const { item, reason, listId } = action.payload;
          if (reason === RequestDigitisationFinishedReason.COMPLETED) {
            return new CompleteRequestDigitisationAction({
              item,
              listId,
            });
          } else {
            return new CancelRequestDigitisationAction();
          }
        }),
      ),
    );

  fetchDcSettings$: Observable<
    FetchDcSettingsSuccessAction | FetchDcSettingsFailedAction
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(RequestDigitisationActionTypes.FETCH_DC_SETTINGS),
      switchMap(() => {
        return this.service.getDcSettings().pipe(
          map(dcSettings => {
            return new FetchDcSettingsSuccessAction({ dcSettings });
          }),
          catchError(error =>
            observableOf(new FetchDcSettingsFailedAction({ error })),
          ),
        );
      }),
    ),
  );
}
